// routes/Routes.jsx
import React, { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import AdminLayout from "../layouts/AdminLayout";
import ScrollToTop from "../components/ScrollToTop";
import Loading from "../components/Loading"; // 🔥 Import Loading Component
import ProtectedRoute from "./ProtectedRoute";

// ✅ Lazy load pages for better performance
const Home = lazy(() => import("../pages/CustomerPages/CustomerHomePage"));
const NotFound = lazy(() => import("../pages/CustomerPages/NotFound"));
const UserProfile = lazy(() => import("../pages/CustomerPages/UserProfilePage"));
const LoginPage = lazy(() => import("../pages/CustomerPages/LoginPage"));
const OTPVerificationPage = lazy(() => import("../pages/CustomerPages/OTPpage"));
const ProductPage = lazy(() => import("../pages/CustomerPages/ProductsPage"));
const ProductDetailsPage = lazy(() => import("../pages/CustomerPages/ProductDetailsPage"));
const CartPage = lazy(() => import("../pages/CustomerPages/CartPage"));
const CheckoutPage = lazy(() => import("../pages/CustomerPages/checkoutpage"));
const RatePage = lazy(() => import("../pages/CustomerPages/RatePage"));
const NotificationsPage = lazy(() => import("../pages/CustomerPages/NotificationsPage"));
const LoyaltyPage = lazy(() => import("../pages/CustomerPages/LoyaltyPage"));
const AboutUs = lazy(() => import("../pages/CustomerPages/AboutUsPage"));
const ContactUs = lazy(() => import("../pages/CustomerPages/ContactUsPage"));
const AdminDashboard = lazy(() => import("../pages/AdminPages/AdminDashboard"));
const BillingPage = lazy(() => import("../pages/AdminPages/BillingPage"));
const InvoicePreview = lazy(() => import("../pages/AdminPages/InvoicePreview"));
const StocksPage = lazy(() => import("../pages/AdminPages/StocksPage"));
const AddStockForm = lazy(() => import("../pages/AdminPages/AddStockForm"));
const StockSuccessPage = lazy(() => import("../pages/AdminPages/StockSuccessPage"));
const StockDetailPage = lazy(() => import("../pages/AdminPages/StockDetailsPage"));
const BannerUpdatePage = lazy(() => import("../pages/AdminPages/BannerUpdatePage"));
const CustomerOrdersPage = lazy(() => import("../pages/AdminPages/CustomerOrdersPage"));
const OrderDetails = lazy(() => import("../pages/AdminPages/OrderDetailsPage"));
const AllCustomers = lazy(() => import("../pages/AdminPages/AllCustomersPage"));
const AdminLoyalty = lazy(() => import("../pages/AdminPages/AdminLoyaltyPage"));
const AdminGoldRate = lazy(() => import("../pages/AdminPages/AdminGoldRate"));
const AdminProfilePage = lazy(() => import("../pages/AdminPages/AdminProfilePage"));
const ReportGenerationPage = lazy(() => import("../pages/AdminPages/ReportGenerationPage"));
const CusOrderDetailsPage = lazy(() => import("../pages/CustomerPages/CusOrderDetailsPage"));
const AddCustomer = lazy(() => import("../pages/AdminPages/AddCustomer"));
const Settings = lazy(()=> import("../pages/AdminPages/AdminConfig"))
const BulkOfferMessage = lazy(()=> import("../pages/AdminPages/BulkOfferMessage"))
const AdminCategoryManager  = lazy(()=> import("../pages/AdminPages/AdminCategoryManager"))
const SmsSettings  = lazy(()=> import("../pages/AdminPages/SettingsPage"))
const Privacy = lazy(()=> import("../pages/CustomerPages/privacyAndPolicy"))
const Terms = lazy(()=> import("../pages/CustomerPages/TermAndConditions"))

const AppRoutes = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  //  Show loading when route changes
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 750); //  Simulate 1 second loading time
    return () => clearTimeout(timer);
  }, [location]);

  return (
    <>
      <ScrollToTop />
      {loading && <Loading />} {/* Show loading screen */}

      <Suspense fallback={<Loading />}> {/*  Suspense for Lazy Loading */}
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verification" element={<OTPVerificationPage />} />
          <Route path="/" element={<LoginPage />} />

          {/* CUSTOMER ROUTES */}
          <Route path="/home" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><Home /></MainLayout></ProtectedRoute>} />

          <Route path="/profile" element={<ProtectedRoute requiredRole="CUSTOMER"><UserProfile /></ProtectedRoute>} />

          <Route path="/categories" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><ProductPage /></MainLayout></ProtectedRoute>} />

          <Route path="/product/:id" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><ProductDetailsPage /></MainLayout></ProtectedRoute>} />

          <Route path="/cart" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><CartPage /></MainLayout></ProtectedRoute>} />

          <Route path="/checkout" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><CheckoutPage /></MainLayout></ProtectedRoute>} />

          <Route path="/rate" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><RatePage /></MainLayout></ProtectedRoute>} />

          <Route path="/notifications" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><NotificationsPage /></MainLayout></ProtectedRoute>} />

          <Route path="/loyalty" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><LoyaltyPage /></MainLayout></ProtectedRoute>} />

          <Route path="/about-us" element={<AboutUs />}/>

          <Route path="/contact-us" element={<ContactUs />} />
          
          <Route path="/customer-order/:Id" element={<ProtectedRoute requiredRole="CUSTOMER"><MainLayout><CusOrderDetailsPage /></MainLayout></ProtectedRoute>} />

          <Route path="/privacy-policy" element={<Privacy />}/>

          <Route path="/terms-conditions" element={<Terms />} />

          {/*  ADMIN ROUTES */}
          <Route path="/admin-dashboard" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />

          <Route path="/admin-billing" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><BillingPage /></AdminLayout></ProtectedRoute>} />

          <Route path="/invoice-preview" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><InvoicePreview /></AdminLayout></ProtectedRoute>} />

          <Route path="/stocks" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><StocksPage /></AdminLayout></ProtectedRoute>} />

          <Route path="/add-stock" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><AddStockForm /></AdminLayout></ProtectedRoute>} />

          <Route path="/stock-success" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><StockSuccessPage /></AdminLayout></ProtectedRoute>} />

          <Route path="/stock-details/:stockId" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><StockDetailPage /></AdminLayout></ProtectedRoute>} />

          <Route path="/banners" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><BannerUpdatePage /></AdminLayout></ProtectedRoute>} />

          <Route path="/orders" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><CustomerOrdersPage /></AdminLayout></ProtectedRoute>} />

          <Route path="/order-details/:id" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><OrderDetails /></AdminLayout></ProtectedRoute>} />

          <Route path="/loyalty-customers" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><AllCustomers /></AdminLayout></ProtectedRoute>} />

          <Route path="/loyalty-customers/:Id" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><AdminLoyalty /></AdminLayout></ProtectedRoute>} />
          
          <Route path="/update-rate" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><AdminGoldRate /></AdminLayout></ProtectedRoute>} />

          <Route path="/admin-profile" element={<ProtectedRoute requiredRole="ADMIN"><AdminProfilePage /></ProtectedRoute>} />

          <Route path="/generate-report" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><ReportGenerationPage /></AdminLayout></ProtectedRoute>} />

          <Route path="/add-customer" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><AddCustomer /></AdminLayout></ProtectedRoute>} />

          <Route path="/sms-settings" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><SmsSettings /></AdminLayout></ProtectedRoute>} />

          <Route path="/OfferMessage" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><BulkOfferMessage /></AdminLayout></ProtectedRoute>} />

          <Route path="/settings" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><Settings /></AdminLayout></ProtectedRoute>} />

          <Route path="/manage-category" element={<ProtectedRoute requiredRole="ADMIN"><AdminLayout><AdminCategoryManager /></AdminLayout></ProtectedRoute>} />


          <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
        </Routes>
      </Suspense>
    </>
  );
};

export default AppRoutes;
